import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Label } from "@/components/ui/label";
import { Delete } from "lucide-react";
import { formatInput } from "@/lib/currency";
import { evalExpression } from "@/lib/calcExpression";
import { cn } from "@/lib/utils";

// Amount input with a safe inline calculator (+ − × ÷). Stores ONLY the final
// numeric result via onChange. Used for transaction Jumlah (Pemasukan/Pengeluaran).

// Build a nicely formatted expression string for "riwayat ekspresi", or null if
// the input is just a single number / not a real (binary) expression.
function buildExpr(text) {
  if (!evalExpression(text).ok) return null;
  const s = String(text)
    .replace(/×/g, "*").replace(/÷/g, "/").replace(/\s+/g, "")
    .replace(/\./g, "").replace(/,/g, ".");
  if (!/[0-9)][+\-*/]/.test(s)) return null; // no binary operator -> single number
  const toks = s.match(/\d+\.?\d*|[+\-*/]/g) || [];
  const glyph = { "*": "×", "/": "÷", "+": "+", "-": "−" };
  return toks
    .map((tk) => (/[+\-*/]/.test(tk) ? glyph[tk] : formatInput(Number(tk))))
    .join(" ");
}

export function CalcAmountInput({ label = "Jumlah", value, onChange, onExpr, initialExpr, testId, className }) {
  const [raw, setRaw] = useState(() => (initialExpr ? initialExpr : value ? formatInput(value) : ""));
  const inputRef = useRef(null);
  const caretToEnd = useRef(false);
  const res = evalExpression(raw);
  const rounded = res.ok ? Math.round(res.value) : 0;

  useEffect(() => {
    const ok = res.ok && rounded > 0;
    onChange(ok ? rounded : 0);
    if (onExpr) onExpr(ok ? buildExpr(raw) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw]);

  // After an operator/backspace/clear tap, keep focus and place caret at the end.
  useLayoutEffect(() => {
    if (caretToEnd.current && inputRef.current) {
      const el = inputRef.current;
      const n = el.value.length;
      el.focus();
      el.setSelectionRange(n, n);
      caretToEnd.current = false;
    }
  }, [raw]);

  const appendOp = (op) => {
    caretToEnd.current = true;
    setRaw((prev) => {
      let p = prev.replace(/\s+$/, "");
      if (p === "") return op === "-" ? "- " : "";
      const last = p[p.length - 1];
      if ("+-*/×÷".includes(last)) p = p.slice(0, -1).replace(/\s+$/, "");
      return `${p} ${op} `;
    });
  };
  const appendDigit = (d) => {
    caretToEnd.current = true;
    setRaw((prev) => prev + d);
  };
  const appendDecimal = () => {
    caretToEnd.current = true;
    setRaw((prev) => {
      const p = prev.replace(/\s+$/, "");
      // Only one decimal comma allowed within the current number segment.
      const seg = p.split(/[+\-*/×÷]/).pop() || "";
      if (seg.includes(",")) return prev;
      if (p === "" || "+-*/×÷".includes(p[p.length - 1])) return `${prev}0,`;
      return `${prev},`;
    });
  };
  const backspace = () => {
    caretToEnd.current = true;
    setRaw((p) => p.replace(/\s+$/, "").slice(0, -1).replace(/\s+$/, ""));
  };
  const clearAll = () => {
    caretToEnd.current = true;
    setRaw("");
  };

  const hasContent = raw.trim() !== "";
  let preview = null;
  if (hasContent) {
    if (res.ok && rounded > 0)
      preview = { cls: "text-emerald-600", text: `= Rp${formatInput(rounded)}`, tid: "calc-result" };
    else if (res.ok && rounded <= 0)
      preview = { cls: "text-amber-600", text: "Hasil harus lebih dari 0", tid: "calc-result-warn" };
    else if (res.error === "divzero")
      preview = { cls: "text-rose-600", text: "Tidak bisa dibagi 0", tid: "calc-result-error" };
    else
      preview = { cls: "text-rose-600", text: "Ekspresi tidak valid", tid: "calc-result-error" };
  }

  return (
    <div className={cn("space-y-1.5", className)}>
      {label && <Label className="font-heading text-sm font-medium text-zinc-700">{label}</Label>}
      <div className="relative">
        <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 font-mono text-sm text-zinc-400">Rp</span>
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          maxLength={40}
          data-testid={testId}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder="cth. 150000 + 275000"
          className="w-full rounded-2xl border border-zinc-200 bg-white py-3.5 pl-11 pr-4 text-right font-mono text-lg font-semibold text-zinc-900 outline-none transition-colors focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
        />
      </div>

      {/* Full calculator pad (0–9 + operators) — hitung tanpa keyboard HP */}
      <div className="space-y-1.5 pt-1" data-testid="calc-pad">
        <div className="grid grid-cols-2 gap-1.5">
          <button
            type="button"
            data-testid="calc-op-backspace"
            onMouseDown={(e) => e.preventDefault()}
            onClick={backspace}
            className="flex h-10 items-center justify-center gap-1.5 rounded-xl border border-zinc-200 bg-white text-sm font-semibold text-zinc-600 transition-colors hover:bg-zinc-50 active:bg-zinc-100"
            aria-label="Hapus satu karakter"
          >
            <Delete className="h-4 w-4" /> Hapus
          </button>
          <button
            type="button"
            data-testid="calc-op-clear"
            onMouseDown={(e) => e.preventDefault()}
            onClick={clearAll}
            className="h-10 rounded-xl border border-rose-200 bg-rose-50 text-sm font-bold text-rose-600 transition-colors hover:bg-rose-100 active:bg-rose-200"
            aria-label="Bersihkan"
          >
            C
          </button>
        </div>

        <div className="grid grid-cols-4 gap-1.5" data-testid="calc-ops">
          {[
            ["7", "8", "9", { g: "÷", t: "divide" }],
            ["4", "5", "6", { g: "×", t: "times" }],
            ["1", "2", "3", { g: "-", t: "minus" }],
            ["0", "000", ",", { g: "+", t: "plus" }],
          ].map((row, ri) => (
            <React.Fragment key={ri}>
              {row.map((cell) => {
                if (typeof cell === "object") {
                  return (
                    <button
                      key={cell.t}
                      type="button"
                      data-testid={`calc-op-${cell.t}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => appendOp(cell.g)}
                      className="h-11 rounded-xl border border-emerald-200 bg-emerald-50 font-mono text-lg font-bold text-emerald-700 transition-colors hover:bg-emerald-100 active:bg-emerald-200"
                    >
                      {cell.g}
                    </button>
                  );
                }
                if (cell === ",") {
                  return (
                    <button
                      key="decimal"
                      type="button"
                      data-testid="calc-decimal"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={appendDecimal}
                      className="h-11 rounded-xl border border-zinc-200 bg-white font-mono text-lg font-bold text-zinc-700 transition-colors hover:bg-zinc-50 active:bg-zinc-100"
                    >
                      ,
                    </button>
                  );
                }
                return (
                  <button
                    key={cell}
                    type="button"
                    data-testid={`calc-digit-${cell}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => appendDigit(cell)}
                    className="h-11 rounded-xl border border-zinc-200 bg-white font-mono text-lg font-bold text-zinc-800 transition-colors hover:bg-zinc-50 active:bg-zinc-100"
                  >
                    {cell}
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {preview ? (
        <p className={cn("pt-0.5 text-right font-mono text-sm font-bold", preview.cls)} data-testid={preview.tid}>
          {preview.text}
        </p>
      ) : (
        <p className="pt-0.5 text-right text-xs text-zinc-400">Bisa hitung langsung, mis. 125000 + 75000</p>
      )}
    </div>
  );
}
